export enum $scannableDocumentReportRuleResult {
  result = 'result',
  scannableDocumentRuleId = 'scannableDocumentRuleId',
  findings = 'findings',
}

import { MimeTypes, MimeTypeToFileExtension } from './mime-type';

export enum $scannableDocument {
  file = 'file',
  _id = '_id',
  status = 'status',
  uploadedAt = 'uploadedAt',
  fileType = 'fileType',
  workspace = 'workspace',
  metadata = 'metadata',
  report = 'report',
  limit = 'limit',
  skip = 'skip',
  entriesPerPage = 'entriesPerPage',
  url = 'url',
  scan = 'scan',
  remediationRequestedAt = 'remediationRequestedAt',
  remediationRequestedBy = 'remediationRequestedBy',
  remediatedDocumentId = 'remediatedDocumentId',
  remediatedDocument = 'remediatedDocument',
  originalDocumentId = 'originalDocumentId',
  deletedAt = 'deletedAt',
  isRemediation = 'isRemediation',
  foundInPages = 'foundInPages',
  title = 'title',
  rawResults = 'rawResults',
  lastError = 'error',
  tenantId = 'tenantId',
  autoRemediation = 'autoRemediation',
}

export enum $scannableDocumentSearch {
  file = 'file',
}

export enum $scannableDocumentRequest {
  files = 'files',
  url = 'url',
}

export enum $scannableDocumentQuery {
  documentUrl = 'documentUrl',
}

export enum $scannableDocumentResponse {
  scannableDocuments = 'scannableDocuments',
  message = 'message',
}

export enum $scannableDocumentMetadata {
  numberOfPages = 'numberOfPages',
  title = 'title',
  thumbnail = 'thumbnail',
  thumbnailId = 'thumbnailId',
  externalId = 'externalId',
  language = 'language',
  author = 'author',
  description = 'description',
  details = 'details',
  keywords = 'keywords',
  fileSize = 'fileSize',
  tagged = 'tagged',
  producer = 'producer',
  version = 'version',
  createDate = 'createDate',
  modifyDate = 'modifyDate',
}

export enum $scannableDocumentReportFinding {
  scannableDocumentPageId = 'scannableDocumentPageId',
  boundingBox = 'boundingBox',
}

export enum $scannableDocumentPage {
  _id = '_id',
  scannableDocumentId = 'scannableDocumentId',
  pageNumber = 'pageNumber',
  pageImageId = 'pageImageId',
  dpi = 'dpi',
  height = 'height',
  width = 'width',
  tenantId = 'tenantId',
}

export enum $scannableDocuments {
  scannableDocuments = 'scannableDocuments',
  _total = '_total',
}

export enum $scannableDocumentUploadRequest {
  urls = 'urls',
  scanId = 'scanId',
  digitalPropertyId = 'digitalPropertyId',
  remediation = 'remediation',
}

export enum $remediatedDocumentRequest {
  remediatedDocumentId = 'remediatedDocumentId',
  originalScannableDocumentId = 'originalScannableDocumentId',
}

export enum $scannableDocumentReport {
  completedAt = 'completedAt',
  fileName = 'fileName',
  results = 'results',
  evaluatedRules = 'evaluatedRules',
  summary = 'summary',
  scannableDocumentId = 'scannableDocumentId',
}

export enum $scannableDocumentReportSummary {
  failed = 'failed',
  passed = 'passed',
  warn = 'warn',
  total = 'total',
}

export enum $scannableDocumentReportByRule {
  rule = 'rule',
  pages = 'pages',
  scannableDocumentId = 'scannableDocumentId',
}

export enum $scannableDocumentReportPageByRule {
  pageNumber = 'pageNumber',
  pageImageSrc = 'pageImageSrc',
  boundingBox = 'boundingBox',
  findings = 'findings',
}

export enum $scannableDocumentRuleContent {
  title = 'title',
  content = 'content', // Generic content for now
  displayOrder = 'displayOrder',
}

export enum ScannerNames {
  base = 'base', // no such scanner, used just to distinguish base rules from custom ones
  opentext = 'opentext',
  pdfua1 = 'pdfua1',
  pdfua2 = 'pdfua2',
}

export enum $documentScanner {
  _id = '_id',
  name = 'name',
  version = 'version',
}

export enum $scannableDocumentRule {
  _id = '_id',
  name = 'name',
  ruleId = 'ruleId',
  description = 'description',
  scanner = 'scanner',
  scannerVersions = 'scannerVersions',
  isDisabled = 'isDisabled',
  documentType = 'documentType',
  customData = 'customData',
  ruleContent = 'ruleContent',
}

export enum $discoveredScannableDocuments {
  discoveredDocuments = 'discoveredDocuments',
  _total = '_total',
}

export enum $discoveredScannableDocument {
  url = 'url',
  status = 'status',
  fileType = 'fileType',
  foundInPages = 'foundInPages',
  title = 'title',
  fileSize = 'fileSize',
  message = 'message',
  uploadedAt = 'uploadedAt',
}

export enum DiscoveredScannableDocumentStatus {
  new = 'new',
  downloading = 'downloading',
  download_error = 'download_error',
  submitted_for_testing = 'submitted_for_testing',
}

export enum $opentextDocumentRule {
  _id = '_id',
  matterhornIndex = 'matterhornIndex',
  checkpoint = 'checkpoint',
}

export enum ScannableDocumentStatus {
  not_tested = 'not_tested',
  ready_for_testing = 'ready_for_testing',
  testing = 'testing',
  tested = 'tested',
  test_error = 'test_error',
  new = 'new',
  downloading = 'downloading',
  download_error = 'download_error',
  upload_error = 'upload_error',
  awaiting_remediation = 'awaiting_remediation',
  deleted = 'deleted',
  remediated = 'remediated',
}

export enum ScannableDocumentAction {
  testDocument = 'testDocument',
  viewReport = 'viewReport',
  removeDocument = 'removeDocument',
}

export enum ScannableDocumentTypes {
  PDF = 'PDF',
}

export const SupportedScannableDocumentFileType: Record<ScannableDocumentTypes, MimeTypes> = Object.freeze({
  [ScannableDocumentTypes.PDF]: MimeTypes.PDF,
});

export const SupportedScannableDocumentExtensions: string[] = [MimeTypeToFileExtension[MimeTypes.PDF]];

export const SupportedScannableDocumentMimeTypeToFiletype: Partial<Record<MimeTypes, ScannableDocumentTypes>> = {
  [MimeTypes.PDF]: ScannableDocumentTypes.PDF,
};

export const SupportedScannableDocumentMimeType: string[] = Object.values(SupportedScannableDocumentFileType);

export enum ScannableDocumentJobOperationType {
  downloadDocument = 'downloadDocument',
  scanDocument = 'scanDocument',
}

export enum OpenTextSessionStartTagOption {
  DoNothing = 'DoNothing',
  AutoTag = 'AutoTag',
  PreserveTags = 'PreserveTags',
}

export enum $scannableDocumentRuleWithResult {
  successCriteria = 'successCriteria',
  result = 'result',
  numberOfFindings = 'numberOfFindings',
  pageNumbers = 'pageNumbers',
}

export enum $scannableDocumentUploadState {
  uploadedFile = 'uploadedFile',
  metadata = 'metadata',
  error = 'error',
}

export enum DocumentVersion {
  original = 'original',
  remediated = 'remediated',
  autoRemediated = 'autoRemediated',
}

export enum $scannableDocumentAutoRemediation {
  status = 'status',
  error = 'error',
  filename = 'filename',
  dateStart = 'dateStart',
  veraPdfReport = 'veraPdfReport',
  remediationReport = 'remediationReport',
  pageImages = 'pageImages',
  pageImageIds = 'pageImageIds',
  flowId = 'flow_id',
  remediatedDocumentId = 'remediatedDocumentId',
}

export enum $autoRemediationRuleResult {
  ruleId = 'ruleId',
  previousStatus = 'previousStatus',
  status = 'status',
  displayStatus = 'displayStatus',
  numberOfFindings = 'numberOfFindings',
}

export enum $autoRemediationPageImage {
  pageNumber = 'pageNumber',
  pageImageId = 'pageImageId',
}

export enum $autoRemediationReportByRule {
  scannableDocumentId = 'scannableDocumentId',
  rule = 'rule',
  pages = 'pages',
}

export enum $autoRemediationReportPageByRule {
  pageNumber = 'pageNumber',
  pageImageSrc = 'pageImageSrc',
  findings = 'findings',
}

export enum ScannableDocumentAutoRemediationStatus {
  progress = 'progress',
  done = 'done',
  error = 'error',
}

export const PDFS_TESTED_PREFIX: string = 'PDFS-TESTED';
export const DEFAULT_MAX_PDF_PAGES: number = 50;
export const ABS_MAX_PDF_PAGES: number = 50000;
